import {
  getEFinancingProgramList,
  getELoanPurposeList,
  getEPaymentPlanList,
  getEProvinceList,
} from '@if/core/types'
import {
  ControlledDateOnlyPicker,
  ControlledSelect,
  ControlledTextField,
  useDollarAdornment,
  useNumberOptionEnum,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Button, Grid, Typography } from '@mui/material'
import {
  getECoBorrowerRelationList,
  getEFinancingPlanList,
  getEInterestComputeMethodList,
  getEInterestRoundingMethodList,
  getETermList,
  useDevTools,
} from '@src/types/constants'
import { useState } from 'react'
import { Control, FieldValues, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ListLoanTestCaseDialog from './ListLoanTestcaseDialog'
import { LoanSchemaType, NewLoanDtoSchemaType } from './loan-form-schema'

interface Props<TFieldValues extends FieldValues> {
  control: Control<NewLoanDtoSchemaType, TFieldValues>
  setValue: UseFormSetValue<NewLoanDtoSchemaType>
}

const LoanIdentificationInputRow = <TFieldValues extends FieldValues = FieldValues>({
  control,
  setValue,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const financingProgramOptions = useTranslatedOptionEnum('EFinancingProgram', getEFinancingProgramList(), 'enum')
  const financingPlanOptions = useTranslatedOptionEnum('enum.EFinancingPlan', getEFinancingPlanList())
  const stateIsoOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const loanPurposeIdOptions = useTranslatedOptionEnum('ELoanPurpose', getELoanPurposeList(), 'enum')
  const relationOptions = useTranslatedOptionEnum('enum.ECoBorrowerRelation', getECoBorrowerRelationList())
  const termOptions = useNumberOptionEnum(getETermList())
  const paymentPlanOptions = useTranslatedOptionEnum('EPaymentPlan', getEPaymentPlanList(), 'enum')
  const dollarAdornment = useDollarAdornment()
  const [openLoanTestCaseDialog, setOpenLoanTestCaseDialog] = useState<boolean>(false)

  const onTestSelected = (loanData: LoanSchemaType) => {
    setValue('loanData', loanData, { shouldValidate: true })

    setOpenLoanTestCaseDialog(false)
  }
  const interestRoundingMethodOptions = useTranslatedOptionEnum(
    'enum.interestRoundingMethod',
    getEInterestRoundingMethodList(),
  )
  const interestComputingMethodOptions = useTranslatedOptionEnum(
    'enum.interestComputingMethod',
    getEInterestComputeMethodList(),
  )

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        {useDevTools && (
          <Button
            onClick={() => setOpenLoanTestCaseDialog(true)}
            type="button"
            variant="contained"
            color="secondary"
            sx={{ mb: 1 }}
          >
            test case
          </Button>
        )}
        <Typography variant="h6">{t('loan.identification')}</Typography>
      </Grid>
      <Grid size={3}>
        <ControlledSelect
          control={control}
          name="loanData.financingProgramId"
          label={t('loan.financingProgram')}
          options={financingProgramOptions}
        />
      </Grid>
      <Grid size={3}>
        <ControlledSelect
          control={control}
          name="loanData.financingPlanId"
          label={t('loan.financingPlan')}
          options={financingPlanOptions}
        />
      </Grid>
      <Grid size={3}>
        <ControlledSelect
          control={control}
          name="loanData.stateIso"
          label={t('common.state')}
          options={stateIsoOptions}
        />
      </Grid>
      <Grid size={3}>
        <ControlledSelect
          control={control}
          name="loanData.loanPurposeId"
          label={t('loan.loanPurpose')}
          options={loanPurposeIdOptions}
        />
      </Grid>
      <Grid size={4}>
        <ControlledTextField control={control} name="loanData.contractNumber" label={t('loan.contractNumber')} />
      </Grid>
      <Grid size={4}>
        <ControlledTextField
          control={control}
          name="loanData.creditReferenceNumber"
          type="number"
          label={t('loan.creditReferenceNumber')}
        />
      </Grid>
      <Grid size={4}>
        <ControlledTextField
          control={control}
          name="loanData.creditApplicationId"
          type="text"
          label={t('loan.creditApplicationId')}
        />
      </Grid>
      <Grid size={4}>
        <ControlledTextField control={control} name="loanData.borrowerId" type="number" label={t('loan.borrowerId')} />
      </Grid>
      <Grid size={4}>
        <ControlledTextField control={control} name="loanData.coBorrowerId" type="number" label={t('loan.coBorrowerId')} />
      </Grid>
      <Grid size={4}>
        <ControlledSelect
          control={control}
          name="loanData.coBorrowerRelationId"
          label={t('loan.coBorrowerRelationId')}
          options={relationOptions}
        />
      </Grid>
      <Grid size={12}>
        <Typography variant="h6">{t('loan.configuration')}</Typography>
      </Grid>
      <Grid size={3}>
        <ControlledSelect control={control} name="loanData.term" label={t('loan.term')} options={termOptions} />
      </Grid>
      <Grid size={3}>
        <ControlledSelect
          control={control}
          name="loanData.paymentPlanId"
          label={t('loan.paymentPlan')}
          options={paymentPlanOptions}
        />
      </Grid>
      <Grid size={3}>
        <ControlledTextField
          control={control}
          name="loanData.nsfFee"
          type="number"
          label={t('loan.nsfFee')}
          slotProps={{ input: dollarAdornment }}
        />
      </Grid>
      <Grid size={3}>
        <ControlledTextField
          control={control}
          name="loanData.adminFee"
          type="number"
          label={t('loan.adminFee')}
          slotProps={{ input: dollarAdornment }}
        />
      </Grid>
      <Grid size={6}>
        <ControlledDateOnlyPicker
          control={control}
          name="loanData.activationPlannedOn"
          label={t('loan.activationPlannedOn')}
        />
      </Grid>
      <Grid size={6}>
        <ControlledDateOnlyPicker
          control={control}
          name="loanData.firstPaymentExpectedOn"
          label={t('loan.firstPaymentExpectedOn')}
        />
      </Grid>
      <Grid size={6}>
        <ControlledSelect
          control={control}
          name="loanData.interestRoundingMethod"
          label={t('loan.interestRoundingMethod')}
          options={interestRoundingMethodOptions}
        />
      </Grid>
      <Grid size={6}>
        <ControlledSelect
          control={control}
          name="loanData.interestComputeMethod"
          label={t('loan.interestComputeMethod')}
          options={interestComputingMethodOptions}
        />
      </Grid>
      <ListLoanTestCaseDialog
        onClose={() => setOpenLoanTestCaseDialog(false)}
        open={openLoanTestCaseDialog}
        onTestCaseSelected={onTestSelected}
      />
    </Grid>
  )
}

export default LoanIdentificationInputRow
